/**
 * Thin client for the lore HTTP API. Mirrors the shape of sessionApi.ts —
 * Cognito ID token as bearer, JSON in / JSON out, errors thrown as plain
 * Error instances with the server's `error` field.
 *
 * The lore API is *caller-scoped* on the server side. The same endpoints
 * return different content depending on which Cognito sub the request comes
 * from, and (for the DM) the optional `as` query parameter switches into
 * preview-as-player mode. The client doesn't model "DM vs player" — it just
 * fetches; the server enforces visibility.
 */
import { getIdToken } from './auth';

const BaseUrl = import.meta.env.VITE_API_URL as string;

/**
 * The four lore types we care about. Mirrors the C# `LoreEntryType` enum.
 * Canvas notes are deferred to a placeholder tile in v1 — the Lore page
 * renders them as a disabled card linking to /sheet.
 */
export type LoreEntryType = 'markdown' | 'canvas';

export interface LoreManifestEntry {
  slug: string;
  relativePath: string;
  title: string;
  folder: string;
  type: LoreEntryType;
  summary?: string;
  tags?: string[];
}

export interface LoreManifest {
  entries: LoreManifestEntry[];
}

export interface LoreLinkRef {
  rawTarget: string;
  displayText: string;
  resolvedSlug?: string;
  /**
   * False for both "target doesn't exist" AND "target exists but caller isn't
   * authorised". The two cases MUST stay indistinguishable to prevent players
   * from probing for hidden notes by inspecting which links are clickable.
   */
  visible: boolean;
}

export interface LoreNote {
  slug: string;
  type: LoreEntryType;
  /** Markdown body, frontmatter already stripped and gated blocks removed. */
  content: string;
  frontmatter: {
    players?: { all?: boolean; explicitNone?: boolean; allowed?: string[] };
    reveal?: string;
    summary?: string;
    tags?: string[];
  };
  links: LoreLinkRef[];
  images: string[];
  etag: string;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  if (!BaseUrl) {
    throw new Error('VITE_API_URL is not configured');
  }
  const token = await getIdToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const resp = await fetch(`${BaseUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (resp.status === 204) {
    return undefined as T;
  }

  const text = await resp.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!resp.ok) {
    const msg =
      payload && typeof payload === 'object' && payload && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Lore request failed (${resp.status})`;
    throw new Error(msg);
  }

  return payload as T;
}

/**
 * Fetches the caller-scoped manifest. Pass `refresh=true` to bust the
 * server's 60s warm cache — the DM uses this after editing in Obsidian.
 *
 * `as` is the DM-only preview-as-player parameter. The server silently
 * ignores it for non-DM callers, so the client can pass it freely; the worst
 * a player can do is get back their normal manifest.
 */
export function getManifest(opts?: {
  refresh?: boolean;
  as?: string;
}): Promise<LoreManifest> {
  const params = new URLSearchParams();
  if (opts?.refresh) params.set('refresh', '1');
  if (opts?.as) params.set('as', opts.as);
  const qs = params.toString();
  return request('GET', `/lore/manifest${qs ? `?${qs}` : ''}`);
}

/**
 * Fetches a single note by slug. Slug must be the value from the manifest
 * (it's already URL-encoded per segment); the client just appends it.
 */
export function getNote(slug: string, opts?: { as?: string }): Promise<LoreNote> {
  const params = new URLSearchParams();
  if (opts?.as) params.set('as', opts.as);
  const qs = params.toString();
  return request('GET', `/lore/note/${slug}${qs ? `?${qs}` : ''}`);
}
